import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import { Button } from '@/components/ui/button';
import { LEAD_GEN_VERTICALS, LEAD_GEN_INDEX } from '@/data/lead-gen-content';
import { breadcrumbJsonLd } from '@/lib/jsonld';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function LeadGenIndex() {
  const reduced = useReducedMotion();
  const stg = reduced ? {} : stagger;
  const up = reduced ? {} : fadeUp;
  const initial = reduced ? false : 'hidden';

  const jsonLd = breadcrumbJsonLd([
    { label: 'Home', path: '/' },
    { label: 'Lead Generation' },
  ]);

  return (
    <>
      <SEOHead path="/lead-generation" jsonLd={jsonLd} />
      <PageHeader
        breadcrumbs={[{ label: 'Lead Generation' }]}
        eyebrow={LEAD_GEN_INDEX.eyebrow}
        title="Lead Generation — performance traffic for high-value verticals"
        subtitle={LEAD_GEN_INDEX.intro}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button asChild data-testid="leadgen-apply-cta" className="btn-primary-gradient text-white rounded-xl px-6 py-5 text-[14px] font-semibold border-0">
            <Link to="/apply" className="inline-flex items-center gap-2">Apply as Advertiser <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </div>
      </PageHeader>

      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid="leadgen-index-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="leadgen-cards-grid"
          >
            {LEAD_GEN_VERTICALS.map((v) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.slug} variants={up}>
                  <Link
                    to={`/lead-generation/${v.slug}`}
                    data-testid={`leadgen-card-${v.slug}`}
                    className="
                      group relative block h-full rounded-2xl overflow-hidden
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10
                      backdrop-blur-xl
                      transition-colors hover:border-[#7DDCFF]/35
                      shadow-[0_24px_60px_rgba(0,0,0,0.45)]
                    "
                  >
                    <div className="relative h-[180px] overflow-hidden">
                      <img
                        src={v.heroImage}
                        alt={v.imageCaption}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover scale-[1.02] group-hover:scale-[1.06] transition-transform duration-700"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(5,11,23,0.35) 0%, rgba(5,11,23,0.85) 65%, rgba(5,11,23,1) 100%)',
                        }}
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 mix-blend-screen opacity-40"
                        style={{
                          background: `radial-gradient(circle at 80% 20%, ${v.accent}26, transparent 60%)`,
                        }}
                      />
                      <span
                        className="absolute top-4 left-4 inline-flex h-10 w-10 items-center justify-center rounded-xl border backdrop-blur-md"
                        style={{
                          background: `linear-gradient(180deg, ${v.accent}33, ${v.accent}10)`,
                          borderColor: `${v.accent}66`,
                          color: v.accent,
                          boxShadow: `0 0 22px ${v.accent}55`,
                        }}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="absolute bottom-4 left-4 right-4 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-300/95">
                        {v.eyebrow}
                      </span>
                    </div>
                    <div className="p-6">
                      <h2 className="font-display text-[20px] font-bold tracking-[-0.01em] text-white group-hover:text-[#7DDCFF] transition-colors">
                        {v.title}
                      </h2>
                      <p className="mt-2.5 text-[13.5px] text-slate-300/90 leading-[1.65] line-clamp-3">
                        {v.short}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#22D3EE]">
                        Explore {v.title} <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <CTASection
        eyebrow="Have a lead funnel ready?"
        title="Apply as advertiser to get a real recommendation"
        subtitle="Share your offer, target GEOs, and form details. Our team reviews each application manually."
        primaryTo="/apply"
      />
    </>
  );
}
